import PageLoaderProvider from '@haaretz/s-htz-link/PageLoaderProvider';
import AtomProviders from '@haaretz/s-next-layout/AtomProviders';
import PpScripts from '@haaretz/s-pp-scripts';
import { ConfigContext } from '@haaretz/s-use-config';
import {
  HydrationBoundary,
  keepPreviousData,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import React from 'react';

import type { AppGlobalType } from '@haaretz/s-data-structure-types';
import type { PlatformType, FontVariable, HtzUser } from '@haaretz/s-types';
import type { DehydratedState } from '@tanstack/react-query';
import type { IConfig } from 'config';
import type { AppProps } from 'next/app';
import type { NextFontWithVariable } from 'next/dist/compiled/@next/font/dist/types';
import type { JSX } from 'react';

interface HtzAppProps {
  dehydratedState: DehydratedState;
  user: HtzUser;
  userAgent: string;
  cookie: string;
  platform: PlatformType;
  countryCode: string;
}

type FontSettings = [NextFontWithVariable, FontVariable];

interface Config {
  config: IConfig;
}

interface CreateAppProps extends Config {
  fonts?: FontSettings[];
  Layout?: React.ComponentType<React.PropsWithChildren<unknown>>;
}

let queryClient = queryClientFactory();

function queryClientFactory() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        placeholderData: keepPreviousData,
        retry: false,
        staleTime: 600000,
      },
    },
  });
}

export default function createApp({ config, fonts, Layout = React.Fragment }: CreateAppProps) {
  function HaaretzApp({ Component, pageProps }: AppProps<HtzAppProps>): JSX.Element {
    if (typeof window === 'undefined') {
      // eslint-disable-next-line react-compiler/react-compiler
      queryClient = queryClientFactory();
    }

    const fontVars =
      fonts && fonts.map(font => `${font[1]}:${font[0].style.fontFamily};`).join('\n');
    /* eslint-disable react/no-unknown-property*/
    const globalStyles = (
      <style jsx global>
        {`
          html {
            ${fontVars}
          }
        `}
      </style>
    );
    /* eslint-enable react/no-unknown-property*/

    const initialGlobalStore: AppGlobalType = {
      animationReduced: null,
      articleCoverColor: null,
      articleId: null,
      articleType: null,
      authors: null,
      blockReason: null,
      contrastMode: null,
      countryCode: pageProps.countryCode ?? 'IL',
      page: null,
      pageStatusToken: null,
      pageType: 'PurchasePage',
      paywallType: null,
      platform: pageProps.platform || 'mobile',
      previewId: null,
      renderingKind: null,
      tags: null,
      teasersOnPage: null,
      user: pageProps.user,
      zenMode: null,
    };

    return (
      <ConfigContext value={config}>
        {fontVars ? globalStyles : null}
        <AtomProviders initialGlobalStore={initialGlobalStore}>
          <PageLoaderProvider>
            {/* <HydrateAtoms initialValues={initialStore} /> */}
            <QueryClientProvider client={queryClient}>
              <HydrationBoundary state={pageProps.dehydratedState}>
                <PpScripts />
                <Layout>
                  <Component {...pageProps} />
                </Layout>
              </HydrationBoundary>
              <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-right" />
            </QueryClientProvider>
          </PageLoaderProvider>
        </AtomProviders>
      </ConfigContext>
    );
  }

  return HaaretzApp;
}
